import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";

export default function SidebarLayout({ user: initialUser, onUserChange }) {
  const syncUserChange = (nextUser) => {
    if (typeof onUserChange === "function") {
      onUserChange(nextUser);
    }
  };
  const [user, setUser] = useState(initialUser);
  const [collapsed, setCollapsed] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [profileName, setProfileName] = useState(initialUser?.username || "");
  const [changePassword, setChangePassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [workerHealth, setWorkerHealth] = useState("ok");

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      setCollapsed(true);
    }
  }, []);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetchWithAuth("/api/health");
        if (response && response.ok) {
          const data = await response.json();
          setWorkerHealth(data.worker_status || "down");
        } else {
          setWorkerHealth("down");
        }
      } catch (err) {
        setWorkerHealth("down");
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setUser(initialUser);
    setProfileName(initialUser?.username || initialUser?.name || "");
  }, [initialUser]);

  const avatarLabel = useMemo(() => {
    const base = user?.username || user?.name || user?.email || "";
    return base ? base.charAt(0).toUpperCase() : "U";
  }, [user]);

  const roleLabel = useMemo(() => {
    if (!user?.role) return "";
    return user.role.charAt(0).toUpperCase() + user.role.slice(1);
  }, [user]);

  const isSpecialUser = user?.email === "ratnanjali@islorg.com";

  const navItems = useMemo(
    () => {
      const items = [
        { label: "Dashboard", to: "/dashboard", icon: DashboardIcon },
        { label: "AI Chatbot", to: "/chatbot", icon: ChatIcon },
        // {
        //   label: "Writing Assistant",
        //   to: "/writing-assistant",
        //   icon: WritingIcon,
        // }
      ];

      if (user?.role === "admin" || isSpecialUser) {
        items.push({ label: "Drive Chatbot", to: "/drive-chatbot", icon: CloudIcon });
      }

      if (isSpecialUser) {
        items.push({ label: "Train Chatbot", to: "/train-chatbot", icon: TrainIcon });
      }

      if (user?.role === "admin") {
        items.push({ label: "User Management", to: "/user-management", icon: UsersIcon });
      }

      return items;
    },
    [isSpecialUser, user?.role]
  );

  const visibleNavItems = navItems;

  const handleToggleCollapse = () => {
    setCollapsed(prev => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    setShowProfileForm(false);
    syncUserChange(null);
    navigate("/login");
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();

    if (!profileName && !(changePassword && password)) {
      window.alert("Please provide name or password to update.");
      return;
    }

    if (changePassword && password !== confirmPassword) {
      window.alert("Passwords do not match.");
      return;
    }

    try {
      setSavingProfile(true);
      const payload = { name: profileName };
      if (changePassword) {
        payload.password = password;
      }

      const response = await fetchWithAuth("/api/update-profile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.detail || "Update failed");
        return;
      }

      if (changePassword) {
        window.alert("Password updated. Please log in again.");
        handleLogout();
        return;
      }

      const updatedUser = { ...user, username: profileName };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      syncUserChange(updatedUser);
      window.alert("Profile updated successfully.");
      setShowProfileForm(false);
    } catch (error) {
      window.alert(`Error updating profile: ${error.message}`);
    } finally {
      setSavingProfile(false);
      setPassword("");
      setConfirmPassword("");
      setChangePassword(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-700">
      <aside
        className={`relative flex min-h-screen flex-col bg-white shadow-sm transition-all duration-300 ${collapsed ? "w-0 overflow-hidden border-r-0 pointer-events-none" : "w-72 border-r border-[#e5e7eb]"
          }`}
      >
        {!collapsed && (
          <>
            <div className="flex items-center justify-between px-6 pb-6 pt-8">
              <button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="text-xl font-semibold text-purple-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
              >
                Adira
              </button>
              <button
                type="button"
                onClick={handleToggleCollapse}
                className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                aria-label="Collapse sidebar"
              >
                <CollapseIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center gap-3 px-6 pb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-lg font-semibold text-purple-600">
                {avatarLabel}
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-slate-800">{user?.username || user?.name || user?.email}</p>
                {roleLabel && (
                  <p className="text-xs uppercase tracking-wide text-slate-400">{roleLabel}</p>
                )}
              </div>
            </div>

            <nav className="flex-1 space-y-1 overflow-y-auto px-4 pb-6">
              {visibleNavItems.map(({ label, to, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${isActive
                      ? "bg-purple-100 text-purple-700"
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    }`
                  }
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setShowProfileForm(prev => !prev)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-purple-200 hover:text-purple-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
              >
                <span>Edit Profile</span>
                <ChevronIcon className={`h-4 w-4 transition-transform ${showProfileForm ? "rotate-180" : "rotate-0"}`} />
              </button>
              <div
                className={`mt-3 space-y-3 overflow-hidden transition-all duration-300 ${showProfileForm ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
                  }`}
              >
                <form className="space-y-3 text-sm" onSubmit={handleProfileSubmit}>
                  <div>
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Name
                    </label>
                    <input
                      type="text"
                      value={profileName}
                      onChange={event => setProfileName(event.target.value)}
                      disabled={savingProfile}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={() => {
                        setChangePassword(prev => !prev);
                        setPassword("");
                        setConfirmPassword("");
                      }}
                      disabled={savingProfile}
                      className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                    Change password
                  </label>
                  {changePassword && (
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                          New password
                        </label>
                        <input
                          type="password"
                          value={password}
                          onChange={event => setPassword(event.target.value)}
                          disabled={savingProfile}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-400">
                          Confirm password
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={event => setConfirmPassword(event.target.value)}
                          disabled={savingProfile}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="w-full rounded-lg bg-purple-600 py-2 text-sm font-semibold text-white transition hover:bg-purple-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 disabled:opacity-60"
                  >
                    {savingProfile ? "Saving..." : "Save changes"}
                  </button>
                </form>
              </div>
            </div>

            <div className="border-t border-slate-200 px-6 py-6">
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-lg bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <LogoutIcon className="h-4 w-4" />
                Logout
              </button>
            </div>
          </>
        )}
      </aside>

      {collapsed && (
        <button
          type="button"
          onClick={handleToggleCollapse}
          className="fixed left-4 top-4 z-20 rounded-full bg-white p-2 shadow-md transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
          aria-label="Expand sidebar"
        >
          <ExpandIcon className="h-5 w-5 text-slate-500" />
        </button>
      )}

      <main className="flex-1 min-h-screen overflow-hidden flex flex-col">
        {workerHealth === "down" && (
          <div className="bg-red-50 p-2 text-center text-xs font-medium text-red-600 border-b border-red-200 shadow-sm z-10 flex-shrink-0">
            ⚠️ Warning: Background Sync Worker (Celery/Redis) is currently unavailable. Drive Ingestions and background polling may fail.
          </div>
        )}
        <div className="flex-1 h-full overflow-hidden">
          <Outlet context={{ user, location }} />
        </div>
      </main>
    </div>
  );
}

function CollapseIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  );
}

function ExpandIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function ChevronIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  );
}

function LogoutIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H9m9 0l-2.25 2.25M18 12l-2.25-2.25" />
    </svg>
  );
}

function DashboardIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="11" width="7" height="10" rx="1.5" />
    </svg>
  );
}

function ChatIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7 8h10M7 12h6M4 17l2.5-2.5h11A2.5 2.5 0 0020 12V7.5A2.5 2.5 0 0017.5 5h-11A2.5 2.5 0 004 7.5V17z"
      />
    </svg>
  );
}

function WritingIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 20.5V7.75A2.75 2.75 0 015.75 5h8.5M8 5V3.5A1.5 1.5 0 019.5 2h5A1.5 1.5 0 0116 3.5V5M8 10h8M8 14h5"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18.5 8.5l2 2a1.5 1.5 0 010 2.12l-5.38 5.38a2 2 0 01-.94.53l-3.03.68a.5.5 0 01-.6-.6l.68-3.03a2 2 0 01.53-.94L18.5 8.5z"
      />
    </svg>
  );
}

function CloudIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M7.5 18h9a4.5 4.5 0 001-8.875A6 6 0 005.1 9.9 4.5 4.5 0 007.5 18z"
      />
    </svg>
  );
}

function UsersIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 17v-.5A3.5 3.5 0 0012.5 13h-7A3.5 3.5 0 002 16.5V17M22 17v-.5a3.5 3.5 0 00-2.87-3.44M16 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function TrainIcon({ className }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      className={className}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 19l-1.5 2M16 19l1.5 2M6 7.5h12M6 12h12M9 7.5v-2A2.5 2.5 0 0111.5 3h1A2.5 2.5 0 0115 5.5v2"
      />
      <rect x="4" y="7.5" width="16" height="8" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
